import type React from 'react';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { Share2, Copy, Check, Facebook, MessageCircle, X } from 'lucide-react';

type Props = {
  productName: string;
  productUrl?: string;
  productPrice: string;
  productImage?: string;
};

async function toSharableFile(src: string, filename: string) {
  const r = await fetch(src, { mode: 'cors', credentials: 'omit' });
  const b = await r.blob();
  const type = b.type || 'image/jpeg';
  return new File([b], filename, { type });
}

export const SocialShareButton: React.FC<Props> = ({
  productName,
  productUrl,
  productPrice,
  productImage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [mainLabel, setMainLabel] = useState('Compartilhar');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px) and (pointer:fine)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const resolvedUrl =
    productUrl ?? (typeof window !== 'undefined' ? window.location.href : '');

  const shareText = useMemo(
    () => `${productName} - ${productPrice}`,
    [productName, productPrice]
  );

  const encodedText = useMemo(() => encodeURIComponent(shareText), [shareText]);
  const encodedUrl = useMemo(
    () => encodeURIComponent(resolvedUrl),
    [resolvedUrl]
  );

  const copyToClipboardFallback = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    }
  }, []);

  const handleCopyLink = useCallback(async () => {
    if (!resolvedUrl) return;
    const ok = await copyToClipboardFallback(resolvedUrl);
    setCopied(ok);
    if (ok) {
      setMainLabel('Link copiado');
      setTimeout(() => {
        setCopied(false);
        setMainLabel('Compartilhar');
      }, 1800);
    }
  }, [resolvedUrl, copyToClipboardFallback]);

  const handleNativeShare = useCallback(async () => {
    if (!('share' in navigator)) return false;

    const payload: ShareData = {
      url: resolvedUrl,
      text: shareText,
      title: productName,
    };

    if (productImage) {
      try {
        const file = await toSharableFile(productImage, 'produto.jpg');
        const files = [file];
        if (
          'canShare' in navigator &&
          (navigator as any).canShare?.({ files })
        ) {
          (payload as any).files = files;
        }
      } catch {
        /* noop */
      }
    }

    try {
      await (navigator as any).share(payload);
      return true;
    } catch {
      return false;
    }
  }, [productName, shareText, resolvedUrl, productImage]);

  const openPopup = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const onMainClick = useCallback(async () => {
    if (isDesktop) {
      setIsOpen((v) => !v);
      return;
    }

    const ok = await handleNativeShare();
    if (ok) return;

    await handleCopyLink();
  }, [isDesktop, handleNativeShare, handleCopyLink]);

  const shareOptions = useMemo(
    () => [
      {
        name: 'Copiar Link',
        icon: copied ? Check : Copy,
        action: handleCopyLink,
        color: copied
          ? 'text-emerald-600'
          : 'text-gray-700 hover:text-gray-900',
        bgColor: copied ? 'bg-emerald-50' : 'hover:bg-gray-100',
      },
      {
        name: 'WhatsApp',
        icon: MessageCircle,
        action: () =>
          openPopup(
            `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`
          ),
        color: 'text-green-600 hover:text-green-700',
        bgColor: 'hover:bg-green-50',
      },
      {
        name: 'Facebook',
        icon: Facebook,
        action: () =>
          openPopup(
            `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
          ),
        color: 'text-blue-600 hover:text-blue-700',
        bgColor: 'hover:bg-blue-50',
      },
      {
        name: 'X',
        icon: X,
        action: () =>
          openPopup(
            `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`
          ),
        color: 'text-gray-900 hover:text-black',
        bgColor: 'hover:bg-gray-100',
      },
    ],
    [encodedText, encodedUrl, openPopup, handleCopyLink, copied]
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onMainClick}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="
          flex items-center justify-center
          text-gray-700 bg-white border border-gray-300 shadow-sm
          transition-all duration-200 hover:bg-gray-50

          w-9 h-9 rounded-full p-0 sm:hidden

          sm:w-auto sm:h-auto sm:px-4 sm:py-2 sm:gap-2 sm:rounded-lg sm:flex
        "
      >
        <Share2 size={16} />
        <span className="hidden sm:inline">{mainLabel}</span>
      </button>

      {isDesktop && isOpen && (
        <>
          <button
            type="button"
            aria-label="Fechar menu de compartilhamento"
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div
            role="menu"
            className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
          >
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 mb-2 px-2">
                Compartilhar produto
              </div>
              {shareOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.name}
                    type="button"
                    onClick={() => {
                      option.action();
                      if (option.name !== 'Copiar Link') setIsOpen(false);
                    }}
                    role="menuitem"
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-all duration-200 ${option.color} ${option.bgColor}`}
                  >
                    <Icon size={16} />
                    {option.name === 'Copiar Link' && copied
                      ? 'Copiado!'
                      : option.name}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
